import React from 'react';
import { BrowserRouter, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import { LanguageProvider } from './contexts/LanguageContext';
import { Toaster } from './components/ui/sonner';
import BackgroundMusic from './components/BackgroundMusic';

// Pages
import LandingPage from './pages/LandingPage';
import LoginPage from './pages/LoginPage';
import RegisterPage from './pages/RegisterPage';
import AuthCallback from './pages/AuthCallback';
import SelectGenderPage from './pages/SelectGenderPage';
import SelectCharacterPage from './pages/SelectCharacterPage';
import SelectVoicePage from './pages/SelectVoicePage';
import SelectLevelPage from './pages/SelectLevelPage';
import ChatPage from './pages/ChatPage';
import PremiumPage from './pages/PremiumPage';
import PaymentSuccessPage from './pages/PaymentSuccessPage';
import PaymentCancelPage from './pages/PaymentCancelPage';
import PrivacyPolicyPage from './pages/PrivacyPolicyPage';
import DeleteAccountPage from './pages/DeleteAccountPage';
import VoiceConfigPage from './pages/VoiceConfigPage';
import FaqPage from './pages/FaqPage';
import ContactPage from './pages/ContactPage';
import AdminReviewsPage from './pages/AdminReviewsPage';
import ForgotPasswordPage from './pages/ForgotPasswordPage';
import ResetPasswordPage from './pages/ResetPasswordPage';
import DownloadPage from './pages/DownloadPage';
import AdminStatsPage from './pages/AdminStatsPage';

// Protected Route Component
const ProtectedRoute = ({ children }) => {
  const { isAuthenticated, loading } = useAuth();
  
  if (loading) {
    return (
      <div className="min-h-screen bg-velvet-bg flex items-center justify-center">
        <div className="w-8 h-8 border-2 border-purple-main border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }
  
  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }
  
  return children;
};

// Public Route
const PublicRoute = ({ children }) => {
  const { loading } = useAuth();
  
  if (loading) {
    return (
      <div className="min-h-screen bg-velvet-bg flex items-center justify-center">
        <div className="w-8 h-8 border-2 border-purple-main border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }
  
  return children;
};

function AppRoutes() {
  const location = useLocation();
  // CRITICAL: Detect Google OAuth callback session_id in hash BEFORE normal routing
  if (location.hash?.includes('session_id=')) {
    return <AuthCallback />;
  }
  return (
    <Routes>
      {/* Public Routes */}
      <Route path="/" element={
        <PublicRoute>
          <LandingPage />
        </PublicRoute>
      } />
      <Route path="/login" element={
        <PublicRoute>
          <LoginPage />
        </PublicRoute>
      } />
      <Route path="/register" element={
        <PublicRoute>
          <RegisterPage />
        </PublicRoute>
      } />
      <Route path="/privacy" element={<PrivacyPolicyPage />} />
      <Route path="/delete-account" element={<DeleteAccountPage />} />
      <Route path="/voice-config" element={<VoiceConfigPage />} />
      <Route path="/faq" element={<FaqPage />} />
      <Route path="/contact" element={<ContactPage />} />
      <Route path="/admin/reviews" element={<AdminReviewsPage />} />
      <Route path="/download" element={<DownloadPage />} />
      <Route path="/admin/stats" element={<AdminStatsPage />} />
      
      {/* Protected Routes - Selection Flow */}
      <Route path="/select-gender" element={
        <ProtectedRoute>
          <SelectGenderPage />
        </ProtectedRoute>
      } />
      <Route path="/select-character/:gender" element={
        <ProtectedRoute>
          <SelectCharacterPage />
        </ProtectedRoute>
      } />
      <Route path="/select-level/:gender/:characterId" element={
        <ProtectedRoute>
          <SelectLevelPage />
        </ProtectedRoute>
      } />
      
      {/* Chat */}
      <Route path="/chat/:gender/:characterId/:level" element={
        <ProtectedRoute>
          <ChatPage />
        </ProtectedRoute>
      } />
      
      {/* Legacy routes - redirect to new flow */}
      <Route path="/select-partner" element={<Navigate to="/select-gender" replace />} />
      
      {/* Password Reset */}
      <Route path="/forgot-password" element={<ForgotPasswordPage />} />
      <Route path="/reset-password" element={<ResetPasswordPage />} />
      
      {/* Premium & Payment */}
      <Route path="/premium" element={
        <ProtectedRoute>
          <PremiumPage />
        </ProtectedRoute>
      } />
      <Route path="/payment/success" element={
        <ProtectedRoute>
          <PaymentSuccessPage />
        </ProtectedRoute>
      } />
      <Route path="/payment/cancel" element={
        <ProtectedRoute>
          <PaymentCancelPage />
        </ProtectedRoute>
      } />
      
      {/* Catch all */}
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

function App() {
  return (
    <BrowserRouter>
      <LanguageProvider>
        <AuthProvider>
          <AppRoutes />
          <BackgroundMusic />
          <Toaster 
            position="top-center"
            toastOptions={{
              style: {
                background: '#0A0A0C',
                border: '1px solid rgba(255, 255, 255, 0.1)',
                color: '#F8FAFC'
              }
            }}
          />
        </AuthProvider>
      </LanguageProvider>
    </BrowserRouter>
  );
}

export default App;
